import React, { useState, useContext, useEffect, useRef } from "react";
import Plot from "react-plotly.js";
import { GlobalContext } from "../../App";

export default function NDVIChart() {
  const { chartData, setChart, xValues, yValues } = useContext(GlobalContext);

  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [animFrame, setAnimFrame] = useState(0);
  const animRef = useRef(null);

  useEffect(() => {
    if (chartData && chartData.chartType === "ndvi_line") {
      setClosing(false);
      setAnimFrame(0);
      setTimeout(() => setVisible(true), 20);
      let frame = 0;
      const total = 40;
      animRef.current = setInterval(() => {
        frame++;
        setAnimFrame(frame);
        if (frame >= total) clearInterval(animRef.current);
      }, 18);
    }
    return () => clearInterval(animRef.current);
  }, [chartData]);

  const handleClose = () => {
    setClosing(true);
    setVisible(false);
    setTimeout(() => setChart(null), 350);
  };

  if (!chartData || chartData.chartType !== "ndvi_line") return null;

  let plotX = xValues ?? [];
  let plotY = yValues ?? [];

  if ((!plotX.length || !plotY.length) && chartData.featureProperties) {
    const props = chartData.featureProperties;
    const entries = Object.entries(props)
      .filter(([k]) => k.startsWith("NDVI_"))
      .map(([k, v]) => {
        const datePart = k.replace("NDVI_", "");
        const [dd, mm, yy] = datePart.split(".");
        const fullYear = parseInt(yy, 10) >= 50 ? `19${yy}` : `20${yy}`;
        const dateObj = new Date(`${fullYear}-${mm}-${dd}`);
        return { dateObj, dateStr: `${dd}/${mm}/${fullYear}`, value: parseFloat(v) };
      })
      .filter(({ dateObj, value }) => !isNaN(dateObj.getTime()) && !isNaN(value))
      .sort((a, b) => a.dateObj - b.dateObj);
    plotX = entries.map((e) => e.dateStr);
    plotY = entries.map((e) => e.value);
  }

  const total = plotX.length;
  const visibleCount = Math.max(2, Math.round((animFrame / 40) * total));
  const animX = plotX.slice(0, visibleCount);
  const animY = plotY.slice(0, visibleCount);

  const minY = Math.min(...plotY);
  const maxY = Math.max(...plotY);
  const farmId = chartData.farmId ?? "Unknown";

  const markerColors = animY.map((v) => {
    if (v <= 0)   return "#f87171";
    if (v <= 0.2) return "#fbbf24";
    if (v <= 0.4) return "#a3e635";
    return "#4ade80";
  });

  const trace = {
    type: "scatter",
    mode: "lines+markers",
    x: animX,
    y: animY,
    line: { color: "#38bdf8", width: 2, shape: "spline", smoothing: 1.1 },
    marker: {
      size: 6,
      color: markerColors,
      line: { color: "rgba(0,0,0,0.4)", width: 1 },
    },
    fill: "tozeroy",
    fillcolor: "rgba(56,189,248,0.07)",
    name: "NDVI",
    hovertemplate: "<b>%{x}</b><br>NDVI: <b>%{y:.3f}</b><extra></extra>",
  };

  const layout = {
    autosize: true,
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    margin: { l: 50, r: 20, t: 20, b: 100 },
    font: { family: "'DM Mono', monospace", size: 11, color: "#64748b" },
    xaxis: {
      title: { text: "Date", font: { size: 11, color: "#fff" }, standoff: 14 },
      showgrid: true,
      gridcolor: "rgba(100,116,139,0.12)",
      tickangle: -50,
      tickfont: { size: 9, color: "#fff" },
      linecolor: "rgba(100,116,139,0.15)",
      zeroline: false,
      nticks: Math.min(total, 20),
      automargin: true,
    },
    yaxis: {
      title: { text: "NDVI", font: { size: 11, color: "#fff" } },
      showgrid: true,
      gridcolor: "rgba(100,116,139,0.12)",
      tickfont: { size: 10, color: "#fff" },
      linecolor: "rgba(100,116,139,0.15)",
      zeroline: true,
      zerolinecolor: "rgba(100,116,139,0.2)",
      zerolinewidth: 1,
      range: [Math.min(minY - 0.05, -0.1), Math.max(maxY + 0.08, 0.8)],
    },
    showlegend: false,
    hovermode: "x unified",
    hoverlabel: {
      bgcolor: "#0f172a",
      bordercolor: "#38bdf8",
      font: { family: "'DM Mono', monospace", size: 12, color: "#e2e8f0" },
    },
    shapes: [
      {
        type: "rect", xref: "paper", yref: "y",
        x0: 0, x1: 1, y0: 0.4, y1: Math.max(maxY + 0.08, 0.8),
        fillcolor: "rgba(74,222,128,0.03)", line: { width: 0 }, layer: "below",
      },
      {
        type: "rect", xref: "paper", yref: "y",
        x0: 0, x1: 1, y0: 0, y1: 0.4,
        fillcolor: "rgba(251,191,36,0.03)", line: { width: 0 }, layer: "below",
      },
    ],
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .ndvi-close-btn:hover { background: rgba(248,113,113,0.2) !important; }
        .ndvi-top-glow {
          position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(56,189,248,0.5), transparent);
        }
      `}</style>

      {/* Fixed dock at bottom of screen */}
      <div
        style={{
         position: "fixed",
          bottom: "12px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "calc(100vw - 48px)",
          maxWidth: "1700px",
          height: "clamp(260px, 28vh, 360px)",   
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(160deg, #0d1520 0%, #0a1628 100%)",
            borderTop: "1px solid rgba(56,189,248,0.18)",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.55)",
            boxSizing: "border-box",
            position: "relative",
            overflow: "hidden",
            transform: visible ? "translateY(0)" : "translateY(calc(100% + 12px))",
            opacity: visible ? 1 : 0,
            transition: closing
              ? "transform 0.3s ease-in, opacity 0.3s ease-in"
              : "transform 0.45s cubic-bezier(0.16,1,0.3,1), opacity 0.38s ease",
            pointerEvents: "all",
          }}
        >
          <div className="ndvi-top-glow" />

          {/* Header */}
          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 16px",
              borderBottom: "1px solid rgba(100,116,139,0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div style={{
                width: 26, height: 26, borderRadius: 6,
                background: "rgba(56,189,248,0.1)",
                border: "1px solid rgba(56,189,248,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13,
              }}>🌾</div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 12, fontWeight: 600,
                color: "#e2e8f0", letterSpacing: "0.02em",
              }}>
                Crop Health Analysis
              </div>
            </div>

            <button
              className="ndvi-close-btn"
              onClick={handleClose}
              style={{
                width: 24, height: 24, borderRadius: 6,
                background: "rgba(248,113,113,0.1)",
                border: "1px solid rgba(248,113,113,0.18)",
                color: "#f87171", cursor: "pointer",
                fontSize: 12, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.2s",
              }}
              title="Close"
            >✕</button>
          </div>

          {/* Chart */}
          <div style={{ width: "100%", height: "calc(100% - 46px)" }}>
            <Plot
              data={[trace]}
              layout={layout}
              config={{
                responsive: true,
                displayModeBar: true,
                displaylogo: false,
                modeBarButtonsToRemove: [
                  "zoom2d", "select2d", "lasso2d",
                  "autoScale2d", "toImage",
                ],
                toImageButtonOptions: {
                  format: "jpeg",
                  filename: `crop_health_farm_${farmId}`,
                  height: 500, width: 1000, scale: 2,
                },
              }}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler
            />
          </div>
        </div>
      </div>
    </>
  );
}